// Landslide forecast advisory PDF — same template as the Fire Risk forecast
// advisory (orange header, summary card, map, four count cards, key table,
// blue footer). Rows are ordered High, Medium, Low, No Risk and the key table
// lists only the risk levels picked in the report dialog (High + Medium by
// default).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { preloadReportLogos, COLORS, HEADER_H } from "../../../common/reportTemplate/reportTemplate.js";
import {
    drawFireHeader, drawFireSummaryCard, drawRiskCountCards, drawTwoToneHeading, drawFireFooter,
    formatIssuedAt, FR_CONTENT_BOTTOM,
} from "../../FireRisk/Forecast/pdfLayout_FR.js";
import { landslideCharacteristicsTranslations, characteristicColors } from "./Translations.js";

// Backend advisory text -> risk key
export const getLandslideCharacteristic = (advisory) => {
    if (advisory) {
        const a = advisory.toLowerCase();
        if (a.includes("high")) return "High";
        if (a.includes("medium")) return "Medium";
        if (a.includes("low")) return "Low";
        if (a.includes("no")) return "NoRisk";
    }
    return "N/A";
};

// Strongest first — the order used for sorting everywhere.
export const RISK_ORDER = ["High", "Medium", "Low", "NoRisk"];
export const riskRank = (risk) => {
    const i = RISK_ORDER.indexOf(risk);
    return i === -1 ? RISK_ORDER.length + 1 : i + 1; // 1..4, N/A last (never 0)
};

const RISK_CARDS = {
    High: { hex: "#E62D3A", bg: COLORS.cardPink },
    Medium: { hex: "#F39B3A", bg: COLORS.cardOrange },
    Low: { hex: "#F3EB12", bg: COLORS.cardTan },
    NoRisk: { hex: "#94CB55", bg: COLORS.cardGreen },
};

const TEXT = {
    en: {
        footer: "Stay alert during heavy rain - avoid steep slopes, rivers and drainage lines.",
        page: "Page [i] of [pageCount]",
        noRows: "No sucos in the selected risk levels",
    },
    tetum: {
        footer: "Tau matan bainhira udan boot - evita foho-sorin, mota no kanal bee.",
        page: "Pajina [i] husi [pageCount]",
        noRows: "Laiha suco iha nivel risku ne'ebé hili",
    },
};

// ── Map: WMS forecast (+ optional household) layers + boundaries ────────────
const MAP_W = 1200;
// Frame runs to -9.75 so the south coast isn't cropped.
const MAP_H = 525;
const BBOX = { minLng: 123.5, maxLng: 127.5, minLat: -9.75, maxLat: -8.0 };

const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

const fetchWmsImage = async (base, layer, style) => {
    const params = new URLSearchParams({
        service: "WMS", version: "1.1.0", request: "GetMap", layers: layer,
        ...(style ? { styles: style } : {}),
        bbox: `${BBOX.minLng},${BBOX.minLat},${BBOX.maxLng},${BBOX.maxLat}`,
        width: String(MAP_W), height: String(MAP_H), srs: "EPSG:4326",
        format: "image/png", transparent: "true",
    });
    try {
        const res = await fetch(`${base}?${params.toString()}`);
        if (!res.ok) return null;
        const blobUrl = URL.createObjectURL(await res.blob());
        const img = await loadImage(blobUrl);
        URL.revokeObjectURL(blobUrl);
        return img;
    } catch (e) {
        console.warn("[Landslide PDF] WMS fetch failed:", layer, e);
        return null;
    }
};

async function buildMapImage({ selectedDate, showForecastLayer, showHouseholdLayer, sucoData, municipalityData, highlightFeature }) {
    const wmsBase = `${process.env.REACT_APP_COMMON_URL}landslide_trigrs/`;
    const [forecastImg, householdImg] = await Promise.all([
        showForecastLayer && selectedDate
            ? fetchWmsImage(wmsBase, `test_lsi:landslide_forecast_${selectedDate}`, "test_lsi:lsi_risk")
            : null,
        showHouseholdLayer && selectedDate
            ? fetchWmsImage(wmsBase, `test_lsi:household_forecast_latest_${selectedDate.replace(/-/g, "")}`, "test_lsi:Household_LSI_Style")
            : null,
    ]);

    const canvas = document.createElement("canvas");
    canvas.width = MAP_W;
    canvas.height = MAP_H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f2f2f2";
    ctx.fillRect(0, 0, MAP_W, MAP_H);
    if (forecastImg) ctx.drawImage(forecastImg, 0, 0, MAP_W, MAP_H);
    if (householdImg) ctx.drawImage(householdImg, 0, 0, MAP_W, MAP_H);

    const px = (lng, lat) => [
        ((lng - BBOX.minLng) / (BBOX.maxLng - BBOX.minLng)) * MAP_W,
        ((BBOX.maxLat - lat) / (BBOX.maxLat - BBOX.minLat)) * MAP_H,
    ];
    const trace = (rings) => rings.forEach((ring) => {
        ring.forEach(([lng, lat], i) => {
            const [x, y] = px(lng, lat);
            if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        ctx.closePath();
    });
    const outline = (geom, style, width) => {
        if (!geom) return;
        ctx.save();
        ctx.strokeStyle = style;
        ctx.lineWidth = width;
        ctx.beginPath();
        if (geom.type === "Polygon") trace(geom.coordinates);
        else if (geom.type === "MultiPolygon") geom.coordinates.forEach(trace);
        ctx.stroke();
        ctx.restore();
    };
    (sucoData?.features || []).forEach((f) => outline(f.geometry, "rgba(0, 0, 0, 0.6)", 0.7));
    (municipalityData?.features || []).forEach((f) => outline(f.geometry, "rgba(0, 0, 0, 0.9)", 2));
    if (highlightFeature?.geometry) outline(highlightFeature.geometry, "rgba(255, 255, 255, 0.95)", 4);
    return canvas.toDataURL("image/png", 0.92);
}

// ── Public entry point ──────────────────────────────────────────────────────
/**
 * rows: landslide rows (Municipality, PostAdmin, Suco, admCode,
 * CharacteristicDay1..3); rainfallRows: rows with admCode + rainfallDay1..3;
 * riskLevels: keys to list in the key table (e.g. ["High", "Medium"]).
 */
export async function generateLandslideAdvisory({
    language = "en", selectedDate, selectedDateIndex = 0, rows = [], rainfallRows = [],
    riskLevels = ["High", "Medium"], levelText = "Country Level",
    showForecastLayer = true, showHouseholdLayer = false,
    sucoData, municipalityData, highlightFeature,
}) {
    const t = TEXT[language] || TEXT.en;
    const riskT = landslideCharacteristicsTranslations[language] || landslideCharacteristicsTranslations.en;

    const pdf = new jsPDF("portrait", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const headerHeight = HEADER_H + 5;
    const limitY = FR_CONTENT_BOTTOM;
    const logos = await preloadReportLogos();
    const headerOpts = { title: "TIMOR LESTE LANDSLIDE RISK ADVISORY", issuedAt: formatIssuedAt() };
    const drawHeader = () => drawFireHeader(pdf, logos, headerOpts);
    const addPageWithHeader = () => { pdf.addPage(); drawHeader(); return headerHeight + 5; };

    const dayKey = `CharacteristicDay${selectedDateIndex + 1}`;
    const rainKey = `rainfallDay${selectedDateIndex + 1}`;
    const classified = rows.map((r) => ({ ...r, risk: getLandslideCharacteristic(r[dayKey] || "N/A") }));
    const counts = Object.fromEntries(RISK_ORDER.map((k) => [k, 0]));
    classified.forEach((r) => { if (counts[r.risk] !== undefined) counts[r.risk] += 1; });

    // ── Page 1: header, summary card ────────────────────────────────────────
    drawHeader();
    let currentY = headerHeight + 3;
    const m = moment(selectedDate);
    currentY = drawFireSummaryCard(pdf, currentY, {
        forecastDate: m.isValid() ? m.format("DD MMMM YYYY") : (selectedDate || "N/A"),
        levelText,
        heading: "LANDSLIDE RISK TODAY",
        description: "Forecast assesses the likelihood of landslides across Timor-Leste",
        icon: "slope",
    });
    currentY += 4;

    // ── Map ─────────────────────────────────────────────────────────────────
    try {
        const mapUrl = await buildMapImage({ selectedDate, showForecastLayer, showHouseholdLayer, sucoData, municipalityData, highlightFeature });
        const mapW = 158;
        const mapH = (MAP_H / MAP_W) * mapW;
        pdf.addImage(mapUrl, "PNG", (pageWidth - mapW) / 2, currentY, mapW, mapH);
        currentY += mapH + 7;
    } catch (e) {
        console.warn("[Landslide PDF] map failed:", e);
        pdf.setFont("helvetica", "italic").setFontSize(9).setTextColor(150, 150, 150);
        pdf.text("[Map image could not be generated]", 10, currentY + 5);
        pdf.setTextColor(0, 0, 0);
        currentY += 12;
    }

    // ── Risk count cards ────────────────────────────────────────────────────
    if (currentY + 62 > limitY) currentY = addPageWithHeader();
    currentY += 3;
    drawTwoToneHeading(pdf, currentY, "LANDSLIDE RISK LEVELS : ", "Suco count by forecast category");
    currentY += 6;
    currentY = drawRiskCountCards(pdf, currentY, RISK_ORDER.map((k) => ({
        bg: RISK_CARDS[k].bg,
        circle: RISK_CARDS[k].hex,
        heading: String(riskT[k] || k).toUpperCase(),
        count: counts[k],
    })));
    currentY += 9;

    // ── Key table: risk levels picked in the dialog, High -> No Risk ────────
    const rainByCode = new Map(rainfallRows.map((r) => [r.admCode, r]));
    const picked = classified
        .filter((r) => riskLevels.includes(r.risk))
        .sort((a, b) =>
            riskRank(a.risk) - riskRank(b.risk) ||
            (a.Municipality || "").localeCompare(b.Municipality || "") ||
            (a.PostAdmin || "").localeCompare(b.PostAdmin || "") ||
            (a.Suco || "").localeCompare(b.Suco || "")
        );
    const body = picked.map((r, i) => {
        const raw = rainByCode.get(r.admCode)?.[rainKey];
        const rain = raw !== undefined && raw !== null && raw !== "" && !isNaN(parseFloat(raw))
            ? parseFloat(raw).toFixed(2) : "N/A";
        return [
            i + 1,
            r.Municipality || "N/A",
            r.PostAdmin || "N/A",
            r.Suco || "N/A",
            rain,
            { content: riskT[r.risk] || r.risk, styles: { fillColor: characteristicColors[r.risk] || [222, 222, 223] } },
        ];
    });

    const headCell = (content, span = {}) => ({
        content, ...span, styles: { halign: "center", valign: "middle", fontStyle: "bold" },
    });
    if (currentY + 40 > limitY) currentY = addPageWithHeader();
    drawTwoToneHeading(pdf, currentY, "KEY TABLE : ", "HIGHEST RISK SUCOS");
    currentY += 4;

    autoTable(pdf, {
        startY: currentY,
        head: [
            [
                headCell("Sl No", { rowSpan: 2 }),
                headCell("Municipality", { rowSpan: 2 }),
                headCell("Post Admin", { rowSpan: 2 }),
                headCell("Suco", { rowSpan: 2 }),
                headCell(m.isValid() ? m.format("YYYY-MM-DD") : (selectedDate || ""), { colSpan: 2 }),
            ],
            [headCell("Rainfall (mm)"), headCell("Risk")],
        ],
        body: body.length
            ? body
            : [[{ content: t.noRows, colSpan: 6, styles: { halign: "center" } }]],
        theme: "grid",
        rowPageBreak: "avoid", // never split a row across pages (leaves an empty sliver)
        headStyles: { fillColor: [225, 232, 242], textColor: [0, 0, 0], fontSize: 8.5, cellPadding: { top: 2.2, bottom: 2.2, left: 1.5, right: 1.5 } },
        styles: { fontSize: 8.5, cellPadding: { top: 2.2, bottom: 2.2, left: 1.8, right: 1.8 }, overflow: "linebreak", textColor: [85, 85, 85], lineWidth: 0.25, lineColor: [150, 150, 150], minCellHeight: 8 },
        columnStyles: {
            0: { cellWidth: 10, halign: "center", valign: "middle" },
            1: { cellWidth: 30, halign: "left", valign: "middle" },
            2: { cellWidth: 36, halign: "left", valign: "middle" },
            3: { cellWidth: 42, halign: "left", valign: "middle" },
            4: { cellWidth: 34, halign: "right", valign: "middle" },
            5: { cellWidth: 40, halign: "center", valign: "middle" },
        },
        // Keep rows clear of the footer bar; continuation pages get the header.
        margin: { left: 9, right: 9, top: headerHeight, bottom: pageHeight - FR_CONTENT_BOTTOM },
        didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(); },
    });

    // ── Footer + page label on every page ───────────────────────────────────
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        drawFireFooter(pdf, {
            message: t.footer,
            pageLabel: t.page.replace("[i]", String(i)).replace("[pageCount]", String(pageCount)),
        });
    }

    pdf.save("DRRDSS-Landslide-Forecast-Advisory.pdf");
}
